// utils/cartonReviewState.js
//
// Where an ACTIVE carton stands in the two-lane cycle (done-ordering pack, brief
// 2026-10-17-done-ordering-review): still ordering → «خلصت طلباتي» → the team
// finishes her lines and prepares the carton → the team sends her the review →
// she accepts, complains, or opens ordering again. Pure — plain `{ status,
// isPoola, doneOrderingAt, reviewSentAt, reviewComplaint, reviewMedia, items }`
// objects, so it behaves identically on a pinia-orm OrderBox and on a raw payload.
//
// The backend owns every rule and enforces it (409 with her sentence): this is
// the PWA declining to OFFER a button the server would refuse — never a second
// source of truth. When the two disagree the server wins and the caller re-reads
// the carton.
//
// The line lists are `shipRequestGate`'s (ItemStatusCategories) — no status is
// spelled out here. "Settled" on the server (no line on its way, at least one
// arrived) is the gate's `onItsWayCount === 0 && arrivedCount > 0`.
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import { shipRequestGate } from '@/utils/shipRequestGate.js';

/** The slot states of an ACTIVE carton, in the order they are decided. */
export const CartonReview = Object.freeze({
  /** She said she is done, and the lines are still being read — claim nothing. */
  LOADING: 'loading',
  /** Still ordering: «خلصت طلباتي» is offered, the ask-to-ship gate is the fast lane. */
  OPEN: 'open',
  /** Closed, but a line is still on its way — the team prepares it when they arrive. */
  CLOSED_WAITING: 'closedWaiting',
  /** Closed and settled, the team has not sent the review yet. */
  CLOSED_PREPARING: 'closedPreparing',
  /** The team sent the review: accept, complain, or open ordering again. */
  REVIEW: 'review',
});

const isStamped = (value) => value !== null && value !== undefined && value !== '';

/**
 * @param {object|null|undefined} box the carton (with its `items`)
 * @param {{loading?: boolean}} [options] `loading` — the lines are still being
 *   fetched. A CLOSED carton then answers LOADING whatever its lines say (a
 *   half-loaded carton reads as «nothing on its way» and would announce a
 *   preparing/review state it is not in). An OPEN carton does not depend on its
 *   lines at all, so its button is never held back by the fetch.
 * @returns {null | {
 *   state: string,
 *   onItsWayCount: number,
 *   arrivedCount: number,
 *   missingCount: number,
 *   outOfStock: Array<object>,
 *   media: Array<{id: string|number, type: string, url: string, thumbnailUrl: string|null}>,
 *   complaint: string|null,
 * }}
 *   `null` when the slot is not this helper's: no carton, not ACTIVE (a READY
 *   carton keeps the reopen door as today), or the pre-paid Poola carton (the
 *   desk never reviews it, so «خلصت طلباتي» is not offered there).
 */
export function cartonReviewState(box, { loading = false } = {}) {
  if (!box || Number(box.status) !== OrderBoxStatus.ACTIVE || box.isPoola) return null;

  const gate = shipRequestGate(box.items);
  const base = {
    onItsWayCount: gate.onItsWayCount,
    arrivedCount: gate.arrivedCount,
    missingCount: gate.outOfStock.length,
    outOfStock: gate.outOfStock,
    media: Array.isArray(box.reviewMedia) ? box.reviewMedia : [],
    complaint: isStamped(box.reviewComplaint) ? box.reviewComplaint : null,
  };

  if (!isStamped(box.doneOrderingAt)) return { ...base, state: CartonReview.OPEN };
  if (loading) return { ...base, state: CartonReview.LOADING };
  if (gate.onItsWayCount > 0) return { ...base, state: CartonReview.CLOSED_WAITING };
  if (isStamped(box.reviewSentAt)) return { ...base, state: CartonReview.REVIEW };
  return { ...base, state: CartonReview.CLOSED_PREPARING };
}
